import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { InputAgentOfRecord, InputGroup } from 'types/groupService';
import { bpHistory } from 'global/bpHistory';

type ConfirmNewGroupBaseFields = {
  name: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  primaryPhone: FormControl<string>;
  primaryEmail: FormControl<string>;
  title: FormControl<string>;
  city: FormControl<string>;
  street: FormControl<string>;
  street2: FormControl<string>;
  notes: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  tin?: FormControl<string>;
  agentOfRecord?: FormControl<string>;
};

export class ConfirmNewGroupModalDelegate {
  private _rootStore = getRootStoreInstance();
  public groupForm: FormController<ConfirmNewGroupBaseFields> = FormController.create(
    {
      name: new FormControl<string>('', { validations: ['printableCharacters'] }),
      firstName: new FormControl<string>('', { validations: ['validName'], optional: true }),
      lastName: new FormControl<string>('', { validations: ['validName'], optional: true }),
      primaryPhone: new FormControl<string>('', { validations: ['isValidUSAPhoneNumber'], optional: true }),
      primaryEmail: new FormControl<string>('', { validations: ['isEmailValid'], optional: true }),
      title: new FormControl<string>('', { optional: true }),
      city: new FormControl<string>('', { validations: ['printableCharacters'], optional: true }),
      street: new FormControl<string>('', {
        validations: ['printableCharacters'],
        optional: true,
        requiredWithFields: ['street2'],
      }),
      street2: new FormControl<string>('', { validations: ['printableCharacters'], optional: true }),
      notes: new FormControl<string>('', { optional: true }),
    },
    {
      capabilities: [{ type: 'zip', params: { zip: { optional: true } } }],
      onSubmit: async (): Promise<void> => {
        await runInAction(async () => {
          const groupId = await this._saveGroupOnServer();
          bpHistory.push(`/group/${groupId}?isBuildProposal=true`);
        });
      },
    },
  );

  private _buildAgentOfRecord(): InputAgentOfRecord {
    if (this.useTinStrategy) {
      return {
        id: '',
        name: '',
        tin: this.groupForm.fields.tin?.value || '',
      };
    }
    const agentOfRecordId = this.groupForm.fields.agentOfRecord?.value || '';
    const selectedAgent = this._rootStore.agentsStore.producers.find((agent) => agent.id === agentOfRecordId);
    if (!selectedAgent) {
      throw new Error('There is no agentOfRecord with such id!');
    }
    return {
      id: selectedAgent.id,
      name: selectedAgent.name,
      npn: selectedAgent.sponsorSpecificData?.npn,
    };
  }

  public get availableAgentsOfRecord(): OptionType[] {
    return this._rootStore.agentsStore.producers.map((agent) => {
      return { value: agent.id, label: agent.name };
    });
  }

  public get useTinStrategy(): boolean {
    return this._rootStore.domainStoreV2.allowTinOnlyGroupCreation;
  }

  private async _saveGroupOnServer(): Promise<string> {
    const agentOfRecord = this._buildAgentOfRecord();
    const groupFromForm: InputGroup = {
      name: this.groupForm.fields.name.value,
      contactInfo: {
        firstName: this.groupForm.fields.firstName.value,
        lastName: this.groupForm.fields.lastName.value,
        primaryPhone: this.groupForm.fields.primaryPhone.value,
        primaryEmail: this.groupForm.fields.primaryEmail.value,
        title: this.groupForm.fields.title.value,
        primaryAddress: {
          city: this.groupForm.fields.city.value,
          street: this.groupForm.fields.street.value,
          street2: this.groupForm.fields.street2.value,
          zip: this.groupForm.fields.zip.value,
          state: this.groupForm.fields.state.value,
          county: this.groupForm.fields.county.value,
        },
      },
      effectiveDate: '',
      id: '',
      source: 'AGENT_V2',
      agentOfRecord,
      accountRepresentative: null,

      notes: this.groupForm.fields.notes.value,
    };
    await this._rootStore.groupStore.createGroup(groupFromForm);
    return this._rootStore.groupStore.group.id;
  }

  public setGroupName(groupName: string): void {
    this.groupForm.fields.name.value = groupName;
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    autorun(() => {
      if (this._rootStore.domainStoreV2.allowTinOnlyGroupCreation) {
        this.groupForm.addFormField(
          'tin',
          new FormControl<string>('', {
            validations: ['containsOnlyNumbers'],
            customValidations: [
              {
                check: (value: string): boolean => value.length === 9,
                msg: 'TIN must be 9 digits.',
              },
            ],
            optional: true,
          }),
        );
      } else {
        this.groupForm.addFormField('agentOfRecord', new FormControl<string>(''));
      }
    });
  }
}
